import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createAuth0Client, type Auth0Client, type User as Auth0User } from '@auth0/auth0-spa-js';
import type { User } from '../types';
import { identifyUser, resetUser } from '../lib/posthog';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_TOKEN_STORAGE_KEY = 'auth_token';

interface Auth0RuntimeConfig {
  domain: string;
  clientId: string;
  audience: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
}

function resolveAuth0RuntimeConfig(): Auth0RuntimeConfig {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN?.trim();
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID?.trim();
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE?.trim();
  const redirectUri =
    import.meta.env.VITE_AUTH0_REDIRECT_URI?.trim() || `${window.location.origin}/auth`;
  const postLogoutRedirectUri =
    import.meta.env.VITE_AUTH0_POST_LOGOUT_REDIRECT_URI?.trim() ||
    `${window.location.origin}/auth`;

  if (!domain || !clientId || !audience) {
    throw new Error(
      'Missing Auth0 config. Set VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID, and VITE_AUTH0_AUDIENCE.'
    );
  }

  return {
    domain,
    clientId,
    audience,
    redirectUri,
    postLogoutRedirectUri,
  };
}

function mapAuth0User(user: Auth0User): User {
  return {
    id: user.sub || '',
    email: user.email || '',
    name: user.name || user.nickname || user.email || 'User',
    createdAt: new Date().toISOString(),
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authClientRef = useRef<Auth0Client | null>(null);
  const authConfigRef = useRef<Auth0RuntimeConfig | null>(null);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      try {
        const config = resolveAuth0RuntimeConfig();
        authConfigRef.current = config;

        const client = await createAuth0Client({
          domain: config.domain,
          clientId: config.clientId,
          authorizationParams: {
            audience: config.audience,
            redirect_uri: config.redirectUri,
            scope: 'openid profile email',
          },
          cacheLocation: 'localstorage',
        });
        authClientRef.current = client;

        const query = new URLSearchParams(window.location.search);
        if (query.has('code') && query.has('state')) {
          const callbackResult = await client.handleRedirectCallback();
          const returnTo =
            typeof callbackResult.appState?.returnTo === 'string' &&
            callbackResult.appState.returnTo.length > 0
              ? callbackResult.appState.returnTo
              : '/';
          window.history.replaceState({}, document.title, returnTo);
        }

        const authenticated = await client.isAuthenticated();
        if (!authenticated) {
          localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
          if (!cancelled) {
            setUser(null);
          }
          return;
        }

        const [accessToken, auth0User] = await Promise.all([
          client.getTokenSilently(),
          client.getUser(),
        ]);

        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, accessToken);
        if (!cancelled) {
          const mappedUser = auth0User ? mapAuth0User(auth0User) : null;
          setUser(mappedUser);
          if (mappedUser) {
            identifyUser(mappedUser);
          }
        }
      } catch (err) {
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        if (!cancelled) {
          setUser(null);
          setError(
            err instanceof Error ? err.message : 'Authentication initialization failed'
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const client = authClientRef.current;
    const config = authConfigRef.current;
    if (!client || !config) {
      const message = 'Auth0 is not configured';
      setError(message);
      throw new Error(message);
    }

    setError(null);
    setIsLoading(true);

    const returnTo =
      window.location.pathname === '/auth'
        ? '/'
        : `${window.location.pathname}${window.location.search}${window.location.hash}`;

    try {
      await client.loginWithRedirect({
        appState: { returnTo },
        authorizationParams: {
          connection: 'google-oauth2',
          audience: config.audience,
          redirect_uri: config.redirectUri,
          scope: 'openid profile email',
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed');
      setIsLoading(false);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    const client = authClientRef.current;
    const config = authConfigRef.current;
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    setUser(null);
    resetUser();

    if (!client || !config) {
      return;
    }

    await client.logout({
      logoutParams: {
        returnTo: config.postLogoutRedirectUri,
      },
    });
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    loginWithGoogle,
    logout,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
